import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EmotionTimelineEntry, KeyMoment, SpeechSegment, ToneSegment } from '../../../services/api.service';
import {
  formatSecondsShort,
  formatSpeakerShortName,
  getSegmentEndSec,
  getSegmentStartSec,
  getSpeakerName,
} from '../utils/audio-segments';
import { buildInterruptionMoments, getMomentTagStyle } from '../utils/meeting-analytics';
import { emotionColor } from '../utils/voice-analytics.utils';
import { formatTopicTime } from './speaker-donut-chart.component';
import styles from './meeting-ribbon.module.scss';

/** «Кардиограмма» встречи: спикеры + тональность (или эмоции, задача B2) + перебивания +
 *  ключевые моменты. */

type SpeakerTimeline = {
  name: string;
  color: string;
};

interface Props {
  segments: SpeechSegment[];
  speakerTimelines: SpeakerTimeline[];
  durationSec: number;
  toneSegments: ToneSegment[];
  keyMoments: KeyMoment[];
  /** Задача B2: если непусто — подложка красится по эмоциям (точнее tone_segments),
   *  иначе используется старое поведение (toneSegments). */
  emotionTimeline?: EmotionTimelineEntry[];
  onSeek: (seconds: number) => void;
}

/** Светофорная шкала подложки тональности: зелёный → жёлтый → красный. */
const TONE_UNDERLAY_COLORS: Record<string, string> = {
  constructive: '#22c55e',
  neutral: '#eab308',
  tense: '#ef4444',
};

const FALLBACK_SPEAKER_COLOR = '#a3a3a3';

function toPct(sec: number, durationSec: number): number {
  return Math.max(0, Math.min(100, (sec / durationSec) * 100));
}

const MeetingRibbon = ({
  segments,
  speakerTimelines,
  durationSec,
  toneSegments,
  keyMoments,
  emotionTimeline = [],
  onSeek,
}: Props) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement | null>(null);

  const speakerColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    speakerTimelines.forEach(s => { map[s.name] = s.color; });
    return map;
  }, [speakerTimelines]);

  const speakerSpans = useMemo(
    () => segments
      .map(seg => {
        const startSec = getSegmentStartSec(seg);
        const endSec = getSegmentEndSec(seg);
        const name = getSpeakerName(seg);
        return {
          startSec,
          endSec,
          name,
          color: speakerColorMap[name] || FALLBACK_SPEAKER_COLOR,
        };
      })
      .filter(span => span.endSec > span.startSec && span.startSec < durationSec)
      .sort((a, b) => a.startSec - b.startSec),
    [segments, speakerColorMap, durationSec],
  );

  const toneSpans = useMemo(
    () => toneSegments
      .filter(seg => seg.end > seg.start && seg.start < durationSec)
      .map(seg => ({
        leftPct: toPct(seg.start, durationSec),
        widthPct: Math.max(toPct(seg.end, durationSec) - toPct(seg.start, durationSec), 0.3),
        color: TONE_UNDERLAY_COLORS[seg.tone] || TONE_UNDERLAY_COLORS.neutral,
        tone: seg.tone,
      })),
    [toneSegments, durationSec],
  );

  /** Задача B2: подложка по эмоциям (точнее tone_segments) — если бэкенд посчитал
   *  emotion_analysis.timeline, красим ей вместо старой toneSpans. */
  const emotionSpans = useMemo(
    () => emotionTimeline
      .filter(entry => entry.end > entry.start && entry.start < durationSec)
      .map(entry => ({
        leftPct: toPct(entry.start, durationSec),
        widthPct: Math.max(toPct(entry.end, durationSec) - toPct(entry.start, durationSec), 0.3),
        color: emotionColor(entry.emotion),
        tone: entry.emotion,
      })),
    [emotionTimeline, durationSec],
  );

  const usingEmotionUnderlay = emotionSpans.length > 0;
  const underlaySpans = usingEmotionUnderlay ? emotionSpans : toneSpans;

  const underlayLegendColors = useMemo(() => {
    if (!usingEmotionUnderlay) {
      return [TONE_UNDERLAY_COLORS.constructive, TONE_UNDERLAY_COLORS.neutral, TONE_UNDERLAY_COLORS.tense];
    }
    return Array.from(new Set(emotionSpans.map(s => s.color)));
  }, [usingEmotionUnderlay, emotionSpans]);

  const interruptionSpikes = useMemo(
    () => buildInterruptionMoments(segments)
      .filter(m => m.atSec >= 0 && m.atSec <= durationSec)
      .map(m => ({
        atSec: m.atSec,
        leftPct: toPct(m.atSec, durationSec),
        label: `${formatSecondsShort(m.atSec)} · ${formatSpeakerShortName(m.from)} → ${formatSpeakerShortName(m.to)}`,
      })),
    [segments, durationSec],
  );

  const momentDots = useMemo(
    () => keyMoments
      .filter(m => m.time >= 0 && m.time <= durationSec)
      .map((m, i) => ({
        key: `${m.time}-${i}`,
        time: m.time,
        leftPct: toPct(m.time, durationSec),
        color: getMomentTagStyle(m.tag_type).dot,
        tag: m.tag,
        text: m.text,
        nearLeftEdge: toPct(m.time, durationSec) < 12,
        nearRightEdge: toPct(m.time, durationSec) > 88,
      })),
    [keyMoments, durationSec],
  );

  const seekByRatio = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track || !durationSec) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(ratio * durationSec);
  }, [durationSec, onSeek]);

  if (!durationSec || !speakerSpans.length) return null;

  return (
    <section className={styles.ribbon} aria-label={t('analytics-ribbon.title')}>
      <div className={styles.head}>
        <span className={styles.title}>{t('analytics-ribbon.title')}</span>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.legendSpeakers} aria-hidden>
              {speakerTimelines.slice(0, 4).map(s => (
                <span key={s.name} style={{ backgroundColor: s.color }} />
              ))}
            </span>
            {t('analytics-ribbon.legend-speakers')}
          </span>
          {underlaySpans.length > 0 && (
            <span className={styles.legendItem}>
              <span className={styles.legendTone} aria-hidden>
                {underlayLegendColors.map(color => (
                  <span key={color} style={{ backgroundColor: color }} />
                ))}
              </span>
              {t(usingEmotionUnderlay ? 'analytics-ribbon.legend-emotions' : 'analytics-ribbon.legend-tone')}
            </span>
          )}
          {interruptionSpikes.length > 0 && (
            <span className={styles.legendItem}>
              <span className={styles.legendSpike} aria-hidden />
              {t('analytics-ribbon.legend-interruptions', { n: interruptionSpikes.length })}
            </span>
          )}
          {momentDots.length > 0 && (
            <span className={styles.legendItem}>
              <span className={styles.legendDot} aria-hidden />
              {t('analytics-ribbon.legend-moments', { n: momentDots.length })}
            </span>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {momentDots.length > 0 && (
          <div className={styles.momentsLane}>
            {momentDots.map(dot => (
              <button
                key={dot.key}
                type="button"
                className={styles.momentDot}
                style={{ left: `${dot.leftPct}%`, backgroundColor: dot.color }}
                onClick={() => onSeek(dot.time)}
                aria-label={`${formatTopicTime(dot.time)} · ${dot.text}`}
              >
                <span
                  className={[
                    styles.momentTooltip,
                    dot.nearLeftEdge ? styles.momentTooltipLeft : '',
                    dot.nearRightEdge ? styles.momentTooltipRight : '',
                  ].filter(Boolean).join(' ')}
                  role="tooltip"
                >
                  <span className={styles.momentTooltipTime}>{formatTopicTime(dot.time)}</span>
                  {dot.tag && <span className={styles.momentTooltipTag}>{dot.tag}</span>}
                  <span className={styles.momentTooltipText}>{dot.text}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        <div
          ref={trackRef}
          className={styles.track}
          role="slider"
          aria-label={t('analytics-ribbon.title')}
          aria-valuemin={0}
          aria-valuemax={Math.round(durationSec)}
          aria-valuenow={0}
          tabIndex={0}
          onClick={e => seekByRatio(e.clientX)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSeek(0);
            }
          }}
        >
          <div className={styles.toneLayer} aria-hidden>
            {underlaySpans.map((span, i) => (
              <span
                key={i}
                style={{
                  left: `${span.leftPct}%`,
                  width: `${span.widthPct}%`,
                  backgroundColor: span.color,
                }}
              />
            ))}
          </div>

          <div className={styles.speakerLayer} aria-hidden>
            {speakerSpans.map((span, i) => (
              <span
                key={`${span.startSec}-${i}`}
                title={`${formatSecondsShort(span.startSec)} · ${formatSpeakerShortName(span.name)}`}
                style={{
                  left: `${toPct(span.startSec, durationSec)}%`,
                  width: `${Math.max(toPct(span.endSec, durationSec) - toPct(span.startSec, durationSec), 0.18)}%`,
                  backgroundColor: span.color,
                }}
              />
            ))}
          </div>

          <div className={styles.spikeLayer}>
            {interruptionSpikes.map((spike, i) => (
              <button
                key={`${spike.atSec}-${i}`}
                type="button"
                className={styles.spike}
                style={{ left: `${spike.leftPct}%` }}
                title={spike.label}
                aria-label={spike.label}
                onClick={e => {
                  e.stopPropagation();
                  onSeek(spike.atSec);
                }}
              />
            ))}
          </div>
        </div>

        <div className={styles.timeLabels} aria-hidden>
          <span>0:00</span>
          <span>{formatSecondsShort(durationSec / 2)}</span>
          <span>{formatSecondsShort(durationSec)}</span>
        </div>
      </div>

      <p className={styles.hint}>{t('analytics-ribbon.hint')}</p>
    </section>
  );
};

export default MeetingRibbon;
